import { FC } from 'react';
import { Button, Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import {
  CopyOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FileZipOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

import { downloadFileFromUrl, getFileUrl } from 'utils/file';
import { getIdStart } from 'utils/string';
import { validateLink } from 'utils/link';
import useCopyText from 'hooks/useCopyText';

interface IProps {
  date?: string | null;
  file?: string;
  link?: string;
  appointmentId: string;
}

const FileItem: FC<IProps> = ({ date, file, link, appointmentId }) => {
  const { copyText } = useCopyText();
  const { t } = useTranslation();

  const renderIcon = () => {
    const style = { color: '#ffffff', fontSize: 16 };
    const parts = file?.split('.');
    const format = parts?.[parts.length - 1];

    switch (format) {
      case 'jpeg':
      case 'jpg':
      case 'png':
        return <FileImageOutlined style={style} />;
      case 'dicom':
      case 'dcm':
        return <FileSearchOutlined style={style} />;
      case 'pdf':
        return <FilePdfOutlined style={style} />;
      case 'docx':
      case 'doc':
        return <FileWordOutlined style={style} />;
      case 'xlsx':
      case 'xls':
      case 'csv':
        return <FileExcelOutlined style={style} />;
      case '7z':
      case 'zip':
        return <FileZipOutlined style={style} />;
      default:
        return <FileTextOutlined style={style} />;
    }
  };

  return (
    <>
      <Flex
        align="center"
        gap={16}
        style={{ flex: '1 0 250px', maxWidth: '250px' }}
      >
        <span className="list-item-bg">{renderIcon()}</span>
        <p>
          <span className="list-content-item-title">
            {file ? t('patient.file') : t('patient.link')}
          </span>
          {file ? (
            <span className="list-content-item-value">{file}</span>
          ) : (
            <a
              href={validateLink(link)}
              target="_blank"
              rel="noreferrer"
              className="list-content-item-value"
            >
              {link}
            </a>
          )}
        </p>
      </Flex>
      <p>
        <span className="list-content-item-title">
          {t('patient.appointment_id')}
        </span>
        <Link
          to={`/appointments/details/${appointmentId}`}
          className="list-content-item-value"
        >
          ID: #{getIdStart(appointmentId)}
        </Link>
      </p>
      <p>
        <span className="list-content-item-title">
          {t('patient.attach_date')}
        </span>
        <span className="list-content-item-value">
          {date ? dayjs(date).format(FULL_DATE_FORMAT) : '-'}
        </span>
      </p>
      <div style={{ width: 165, display: 'flex', justifyContent: 'flex-end' }}>
        {file ? (
          <Button
            size="large"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => downloadFileFromUrl(getFileUrl(file), file)}
            ghost
          >
            {t('common.download')}
          </Button>
        ) : link ? (
          <Button
            size="large"
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => copyText(link)}
            ghost
          >
            {t('patient.copy_link')}
          </Button>
        ) : null}
      </div>
    </>
  );
};

export default FileItem;
